import PropTypes from 'prop-types';
import { Box, FormControl, IconButton, Menu, MenuItem, Radio, RadioGroup, Typography, AppBar } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React, { useState } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { AIADMIN_TEXT_SECONDARY } from './CommonThemes';
import Profile from '../../common/header/Profile';
import useHeaderStyles from './HeaderStyles';
import { useTenant } from './components/tenantContext/TenantContext';
import SessionManagement from '../../common/header/SessionManagement';
import { MIXPANEL_EVENTS } from '../../common/MixPanelConstants';
import { getTenantTrackingProperties, trackEvent } from './utils/mixPanelUtils';
import { isAdvancedPromptManagementEnabled } from '../../common/prompt/promptManagementFlag';
import { TELEMETRY_ROUTE_PATH } from '../../common/constants';
import { isOtelTenant } from './telemetry/utils/telemetryUtils';
import {
  isAgenticPromptRoute,
  resolveAgenticPromptFallbackPath,
  sanitizeNavigationPath
} from './utils/moduleSwitcherUtils';

const getScreenRemainder = (pathname) => {
  const segments = (pathname || '').split('/').slice(3).filter(Boolean);
  if (!segments.length) {
    return '';
  }
  return `/${segments.join('/')}`;
};

const getTabPath = (tab) => {
  if (tab?.path) {
    return sanitizeNavigationPath(tab.path);
  }
  if (tab?.tabUrl) {
    return sanitizeNavigationPath(tab.tabUrl);
  }
  return '';
};

const findTabForRoute = (tabDetails, remainder) => {
  if (!remainder) {
    return null;
  }
  const matches = (tabDetails || [])
    .map((tab) => ({ tab, tabPath: getTabPath(tab) }))
    .filter(({ tabPath }) => tabPath && (remainder === tabPath || remainder.startsWith(`${tabPath}/`)))
    .sort((a, b) => b.tabPath.length - a.tabPath.length);
  return matches[0]?.tab || null;
};

const userCanOpenTab = (tab, permissions) => {
  const required = tab?.permissionsRequired;
  if (!required?.length) {
    return true;
  }
  return required.some((permission) => (permissions || []).includes(permission));
};

const getAppliedPermissions = (userPermissionDetail, isSuperAdmin, tenantId) => {
  if (isSuperAdmin) {
    return userPermissionDetail?.superPermissions || [];
  }
  const match = userPermissionDetail?.tenantPermissions?.find((item) => item.tenantId === tenantId);
  return match?.permissions || [];
};

/**
 * Stay on the current screen when the selected tenant offers it and the user can open it.
 * Otherwise land on that tenant's dashboard.
 */
const resolveTenantSwitchPath = ({ pathname, search, nextTenant, isSuperAdmin, userPermissionDetail }) => {
  const tenantName = nextTenant?.name;
  const dashboardPath = tenantName ? `/aiadmin/${tenantName}` : '/aiadmin';
  const remainder = getScreenRemainder(pathname);
  if (!remainder || !tenantName) {
    return { path: dashboardPath, currentTabName: '' };
  }

  const previousTenantName = (pathname || '').split('/')[2] || '';
  if (isAgenticPromptRoute(pathname, previousTenantName) && !isAdvancedPromptManagementEnabled(nextTenant)) {
    return {
      path: resolveAgenticPromptFallbackPath({
        ...nextTenant,
        tenantName,
        tabDetails: nextTenant?.tabDetails
      }),
      currentTabName: ''
    };
  }

  const tab = findTabForRoute(nextTenant?.tabDetails, remainder);
  const permissions = getAppliedPermissions(userPermissionDetail, isSuperAdmin, nextTenant?.tenantId);
  if (tab && userCanOpenTab(tab, permissions)) {
    const currentTabName = tab.tabTitle || tab.tabUrl || tab.tabName || '';
    const tabPath = getTabPath(tab);
    const isTelemetryRoute =
      remainder === `/${TELEMETRY_ROUTE_PATH}` || remainder.startsWith(`/${TELEMETRY_ROUTE_PATH}/`);
    if (isTelemetryRoute && remainder !== tabPath && !isOtelTenant(nextTenant)) {
      return { path: `${dashboardPath}${tabPath}`, currentTabName };
    }
    return {
      path: `${dashboardPath}${remainder}${search || ''}`,
      currentTabName
    };
  }

  return { path: dashboardPath, currentTabName: '' };
};

const Header = ({ onTenantChange, userPermissionDetail, isSuperAdmin, tenantList }) => {
  const history = useHistory();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useHeaderStyles();
  const { tenant } = useTenant();

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTenantChange = (selectedTenantVal) => {
    trackEvent(MIXPANEL_EVENTS.TENANT_SWITCHED, {
      ...getTenantTrackingProperties(selectedTenantVal),
      previousTenantName: tenant.tenantName,
      previousTenantTitle: tenant.tenantTitle
    });
    const { path, currentTabName } = resolveTenantSwitchPath({
      pathname: location.pathname,
      search: location.search,
      nextTenant: selectedTenantVal,
      isSuperAdmin,
      userPermissionDetail
    });
    history.push(path);
    onTenantChange(selectedTenantVal, { currentTabName });
    handleClose();
  };

  const tenantExists = (tenantId) => {
    if (isSuperAdmin) {
      return true;
    }
    const tenantCheck = userPermissionDetail?.tenantPermissions.some((item) => {
      if (item.tenantId) {
        return item?.tenantId === tenantId;
      }
      return true;
    });

    return tenantCheck;
  };

  return (
    <AppBar className={classes.appBar}>
      <Box component="header" className={classes.headerSection}>
        <Link to={`/aiadmin/${tenant.tenantName}`} title="Click here to go to the home page.">
          <Box component="img" src="/images/pearsonlogo.svg" alt="Title text will come" className={classes.logo} />
        </Link>
        <Box className={classes.flexAndCenter}>
          <Box className={classes.flexAndCenter}>
            <Typography className={classes.tenantAbbreviation}>{tenant.tenantTitle}</Typography>
            <IconButton onClick={handleOpen} sx={{ color: AIADMIN_TEXT_SECONDARY }}>
              <ArrowDropDownIcon />
            </IconButton>
          </Box>
          <Profile />
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <FormControl component="fieldset" sx={{ p: 2 }}>
            <RadioGroup value={tenant.tenantId}>
              {tenantList.map(
                (item) =>
                  tenantExists(item?.tenantId) ? ( // check for tenant exist or not
                    <MenuItem key={item.tenantId} value={item.tenantId} onClick={() => handleTenantChange(item)}>
                      <Radio value={item.tenantId} />
                      {item.title}
                    </MenuItem>
                  ) : null // return null
              )}
            </RadioGroup>
          </FormControl>
        </Menu>
      </Box>
      <SessionManagement />
    </AppBar>
  );
};

Header.propTypes = {
  onTenantChange: PropTypes.func.isRequired,
  userPermissionDetail: PropTypes.array.isRequired,
  isSuperAdmin: PropTypes.bool.isRequired,
  tenantList: PropTypes.array.isRequired
};

export default Header;
